import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Real API call to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      console.log('Form submitted successfully:', data);
      
      setSubmitted(true);
      setIsSubmitting(false);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      }, 3000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your inquiry. Please try again or email us directly at contact@taitconsultancy.com');
    }
  };

  if (submitted) {
    return (
      <div 
        className="text-center p-12"
        style={{
          background: 'var(--bg-card)',
          borderRadius: '1rem',
          border: '1px solid var(--border-light)'
        }}
      >
        <div 
          className="mx-auto mb-4"
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '3px solid #D4AF37'
          }}
        >
          <CheckCircle size={32} color="#FFFFFF" />
        </div>
        <h3 className="heading-2 mb-2">Thank You!</h3>
        <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
          We've received your inquiry and will get back to you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block body-small mb-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>
            Your Name *
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="John Doe"
            style={{
              border: '1px solid #D4AF37',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#1a1a1a',
              color: '#FFFFFF'
            }}
          />
        </div>
        <div>
          <label className="block body-small mb-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>
            Email Address *
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@example.com"
            style={{
              border: '1px solid #D4AF37',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#1a1a1a',
              color: '#FFFFFF'
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block body-small mb-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>
            Phone Number
          </label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 (555) 000-0000"
            style={{
              border: '1px solid #D4AF37',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#1a1a1a',
              color: '#FFFFFF'
            }}
          />
        </div>
        <div>
          <label className="block body-small mb-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>
            Project Type
          </label>
          <select
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            style={{
              width: '100%',
              border: '1px solid #D4AF37',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#1a1a1a',
              color: '#FFFFFF',
              fontSize: '1rem'
            }}
          >
            <option value="">Select a service</option>
            <option value="research">AI Research & Presentations</option>
            <option value="chatbot">Chatbot & Automation</option>
            <option value="website">Landing Pages & Websites</option>
            <option value="video">Social Media Videos</option>
            <option value="event">Event Planning</option>
            <option value="design">Art & Design Services</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block body-small mb-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>
          Tell Us About Your Project *
        </label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={6}
          placeholder="Describe what you'd like us to help you with..."
          style={{
            border: '1px solid #D4AF37',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            resize: 'vertical',
            background: '#1a1a1a',
            color: '#FFFFFF'
          }}
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full md:w-auto"
        style={{
          minWidth: '200px',
          background: '#D4AF37',
          color: '#000000'
        }}
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin">⏳</span>
            Sending...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send size={18} />
            Send Inquiry
          </span>
        )}
      </Button>

      <div className="flex flex-col md:flex-row gap-4 pt-6 border-t" style={{ borderColor: '#D4AF37' }}>
        <div className="flex items-center gap-3">
          <div 
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #D4AF37'
            }}
          >
            <Bot size={20} color="#FFFFFF" />
          </div>
          <div>
            <p className="caption" style={{ color: '#888888' }}>Need help with this form?</p>
            <p className="body-small" style={{ color: '#FFFFFF', fontWeight: '500' }}>Chat with Brendan instead →</p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;